<template>
  <div class="oc-step-letter">
    <div class="oc-step-header oc-step-header--compact mb-50">
      <div class="oc-step-header__icon oc-step-header__icon--compact">
        <feather-icon icon="Edit3Icon" size="16" />
      </div>
      <div class="oc-step-header__body flex-grow-1">
        <div class="d-flex flex-wrap align-items-center justify-content-between">
          <div>
            <span class="oc-step-header__title">Write letter</span>
            <span class="oc-step-header__hint">
              · Choose sections &amp; edit content (hidden = omitted from PDF)
            </span>
          </div>
          <b-button
            v-b-tooltip.hover
            size="sm"
            variant="outline-primary"
            class="oc-settings-btn ml-50 mt-25 mt-sm-0"
            title="Stamp & signature settings"
            :disabled="editorDisabled"
            @click="$emit('update:showLetterSettingsModal', true)"
          >
            <feather-icon icon="SettingsIcon" size="14" class="mr-25" />
            Stamp settings
          </b-button>
        </div>
      </div>
    </div>

    <b-alert
      v-if="templateEditor.mode === 'raw'"
      variant="warning"
      show
      class="py-75 mb-1"
    >
      <feather-icon icon="AlertTriangleIcon" size="15" class="mr-50" />
      This template uses a custom layout that could not be split into sections.
      Add <code>&lt;!-- OFFER_* markers --&gt;</code> to the HTML to enable the section editor.
    </b-alert>

    <offer-section-picker
      class="mb-1"
      :sections="letterSectionPickerItems"
      :disabled="editorDisabled"
      @toggle="$emit('toggle-section', $event)"
      @select="$emit('open-section', $event)"
      @show-all="$emit('show-all-sections')"
    />

    <div class="oc-form-section mb-1">
      <div class="oc-form-section__head mb-75">
        <feather-icon icon="TypeIcon" size="15" class="mr-50" />
        Letter headline &amp; defaults
      </div>
      <b-row class="align-items-end">
        <b-col cols="12" md="4" class="mb-50 mb-md-0">
          <b-form-group label="Main letter title" label-for="oc-letter-title-text" class="mb-0">
            <b-form-input
              id="oc-letter-title-text"
              :value="letterTitleText"
              :disabled="editorDisabled"
              placeholder="OFFER LETTER"
              size="sm"
              @input="$emit('update:letterTitleText', $event)"
            />
          </b-form-group>
        </b-col>
        <b-col cols="6" md="2" class="mb-50 mb-md-0">
          <b-form-group label="Title alignment" label-for="oc-letter-title-align" class="mb-0">
            <b-form-select
              id="oc-letter-title-align"
              size="sm"
              :value="sectionLayoutResolved('title').titleAlign || 'center'"
              :disabled="editorDisabled"
              :options="titleAlignOptions"
              @change="$emit('letter-title-align-change', $event)"
            />
          </b-form-group>
        </b-col>
        <b-col cols="6" md="2" class="mb-50 mb-md-0">
          <b-form-group label="Title font" label-for="oc-letter-title-font" class="mb-0">
            <b-form-select
              id="oc-letter-title-font"
              size="sm"
              :value="sectionLayoutResolved('title').fontSizePx"
              :disabled="editorDisabled"
              :options="fontSizeSelectOptions"
              @change="$emit('section-layout-change', 'title', 'fontSizePx', Number($event))"
            />
          </b-form-group>
        </b-col>
        <b-col cols="6" md="2" class="mb-50 mb-md-0">
          <b-form-group label="Body font" label-for="oc-layout-default-font" class="mb-0">
            <b-form-select
              id="oc-layout-default-font"
              size="sm"
              :value="layoutDefaults.fontSizePx"
              :disabled="editorDisabled"
              :options="fontSizeSelectOptions"
              @change="$emit('layout-default-change', 'fontSizePx', Number($event))"
            />
          </b-form-group>
        </b-col>
      </b-row>
    </div>

    <b-modal
      id="oc-letter-settings-modal"
      :visible="showLetterSettingsModal"
      title="Stamp & signature settings"
      size="md"
      ok-only
      ok-title="Done"
      ok-variant="primary"
      modal-class="oc-letter-settings-modal"
      :ok-disabled="editorDisabled"
      @change="$emit('update:showLetterSettingsModal', $event)"
    >
      <offer-signature-settings
        id-prefix="oc-letter"
        embedded
        :signature-locations="offerSignatureLocationsModel"
        :employee-sign="employeeSignPlacement"
        :disabled="editorDisabled"
        @signature-locations-change="$emit('signature-locations-change', $event)"
        @employee-sign-change="onEmployeeSignChange"
      />
    </b-modal>

    <b-alert
      v-if="!includedOrderedLetterSections.length"
      show
      variant="light"
      class="mb-1 oc-empty-sections-alert"
    >
      <feather-icon icon="ToggleLeftIcon" size="16" class="mr-50 text-muted" />
      Turn on at least one section above to start writing.
    </b-alert>

    <div v-else role="tablist" class="oc-letter-sections">
      <b-card
        v-for="(section, idx) in includedOrderedLetterSections"
        :id="`oc-section-${section.sectionKey}`"
        :key="section.sectionKey"
        no-body
        class="oc-letter-section-card mb-75"
        :class="{ 'oc-letter-section-card--open': activeLetterSection === section.sectionKey }"
      >
        <b-card-header
          header-tag="header"
          class="oc-letter-section-header p-75"
          role="tab"
          @click="onLetterSectionHeaderClick(section.sectionKey)"
        >
          <div class="d-flex align-items-center w-100">
            <span class="oc-letter-section-num mr-50">{{ section.number }}</span>
            <span class="oc-letter-section-title flex-grow-1">
              {{ section.label }}
            </span>
            <b-button-group
              size="sm"
              class="oc-letter-section-move flex-shrink-0 ml-50"
              @click.stop
            >
              <b-button
                variant="outline-secondary"
                class="oc-move-btn"
                :disabled="editorDisabled || idx === 0"
                title="Move up"
                @click.stop="$emit('move-section', section.sectionKey, 'up')"
              >
                <feather-icon icon="ChevronUpIcon" size="14" />
              </b-button>
              <b-button
                variant="outline-secondary"
                class="oc-move-btn"
                :disabled="editorDisabled || idx === includedOrderedLetterSections.length - 1"
                title="Move down"
                @click.stop="$emit('move-section', section.sectionKey, 'down')"
              >
                <feather-icon icon="ChevronDownIcon" size="14" />
              </b-button>
            </b-button-group>
            <feather-icon
              :icon="activeLetterSection === section.sectionKey ? 'ChevronUpIcon' : 'ChevronDownIcon'"
              size="16"
              class="oc-letter-section-chevron ml-50 text-muted"
            />
          </div>
        </b-card-header>
        <b-collapse
          :id="letterAccordionId(section.sectionKey)"
          :visible="activeLetterSection === section.sectionKey"
          role="tabpanel"
        >
          <b-card-body class="oc-letter-section-body">
            <p class="text-muted small oc-letter-section-hint mb-75">{{ section.hint }}</p>
            <offer-section-page-controls
              :section-key="section.sectionKey"
              :page-start-mode="sectionPageStartMode(section.sectionKey)"
              :font-size-px="sectionLayoutResolved(section.sectionKey).fontSizePx"
              :section-title-text="sectionLayoutResolved(section.sectionKey).sectionTitleText"
              :section-title-align="sectionLayoutResolved(section.sectionKey).sectionTitleAlign || 'center'"
              :section-title-font-size-px="sectionLayoutResolved(section.sectionKey).sectionTitleFontSizePx || 11"
              :section-title-font-weight="sectionLayoutResolved(section.sectionKey).sectionTitleFontWeight || 'bold'"
              :default-section-title="defaultSectionTitleText[section.sectionKey]"
              :supports-title="section.supportsTitle"
              :disabled="editorDisabled"
              @page-start-change="$emit('page-start-change', section.sectionKey, $event)"
              @font-size-change="$emit('section-layout-change', section.sectionKey, 'fontSizePx', $event)"
              @section-title-change="$emit('section-title-change', section.sectionKey, $event)"
              @section-title-font-size-change="$emit('section-title-font-size-change', section.sectionKey, $event)"
              @section-title-font-weight-change="$emit('section-title-font-weight-change', section.sectionKey, $event)"
              @section-title-align-change="$emit('section-title-align-change', section.sectionKey, $event)"
            />

            <template v-if="section.isClosing">
              <b-form-group
                label="Closing paragraph"
                :label-for="`oc-closing-${section.key}`"
                description="Optional remarks before the signatory block."
                class="mb-1"
              >
                <div class="offer-quill-wrap">
                  <p class="offer-quill-format-hint small text-muted mb-50">
                    <feather-icon icon="InfoIcon" size="13" class="mr-25 align-text-top" />
                    Select text in the editor, then use the formatting tools on the right.
                  </p>
                  <b-row class="offer-quill-grid no-gutters">
                    <b-col cols="12" md="3" class="offer-quill-toolbar-col order-2 order-md-2">
                      <div :id="quillToolbarId('closing')" class="offer-quill-toolbar-slot" />
                    </b-col>
                    <b-col cols="12" md="9" class="offer-quill-editor-col order-1 order-md-1">
                      <quill-editor
                        :id="`oc-closing-${section.key}`"
                        :key="`closing-${quillToolbarId('closing')}-${formId || 'new'}-${editorMountKey}`"
                        :ref="quillRefName('closing')"
                        :value="templateEditor.fields.closing"
                        :options="offerQuillOptions"
                        :disabled="editorDisabled"
                        class="offer-quill-editor offer-quill-editor--closing"
                        @input="$emit('letter-field-input', 'closing', $event)"
                        @ready="() => onQuillReady('closing')"
                      />
                    </b-col>
                  </b-row>
                </div>
              </b-form-group>
              <b-row>
                <b-col cols="12" lg="6">
                  <b-form-group
                    label="Signatory name"
                    label-for="oc-sign-name"
                    description="Company authorised signatory (person or title)."
                  >
                    <b-form-input
                      id="oc-sign-name"
                      v-model="templateEditor.fields.signatoryName"
                      :disabled="editorDisabled"
                      placeholder="e.g. Priya Sharma"
                      @input="$emit('sync-template-from-fields')"
                    />
                  </b-form-group>
                </b-col>
                <b-col cols="12" lg="6">
                  <b-form-group
                    label="Designation"
                    label-for="oc-sign-role"
                    description="Company signatory title only (e.g. Human Resources). Not employee acceptance lines."
                  >
                    <b-form-input
                      id="oc-sign-role"
                      v-model="templateEditor.fields.signatoryDesignation"
                      :disabled="editorDisabled"
                      placeholder="e.g. Human Resources"
                      @input="$emit('sync-template-from-fields')"
                    />
                  </b-form-group>
                </b-col>
              </b-row>
              <p class="small text-muted mb-0">
                Employee Name / Employee Signature lines are controlled in
                <strong>Stamp settings</strong> (top of Write letter) — not in Designation.
              </p>
            </template>
            <template v-else>
              <div class="offer-quill-wrap">
                <p class="offer-quill-format-hint small text-muted mb-50">
                  <feather-icon icon="InfoIcon" size="13" class="mr-25 align-text-top" />
                  Select text in the editor, then use the formatting tools on the right.
                </p>
                <b-row class="offer-quill-grid no-gutters">
                  <b-col cols="12" md="3" class="offer-quill-toolbar-col order-2 order-md-2">
                    <div :id="quillToolbarId(section.field)" class="offer-quill-toolbar-slot" />
                  </b-col>
                  <b-col cols="12" md="9" class="offer-quill-editor-col order-1 order-md-1">
                    <quill-editor
                      :id="`oc-${section.key}`"
                      :key="`${section.key}-${quillToolbarId(section.field)}-${formId || 'new'}-${editorMountKey}`"
                      :ref="quillRefName(section.field)"
                      :value="templateEditor.fields[section.field]"
                      :options="offerQuillOptions"
                      :disabled="editorDisabled"
                      class="offer-quill-editor"
                      @input="$emit('letter-field-input', section.field, $event)"
                      @ready="() => onQuillReady(section.field)"
                    />
                  </b-col>
                </b-row>
              </div>
            </template>
          </b-card-body>
        </b-collapse>
      </b-card>
    </div>
  </div>
</template>

<script>
import { VBTooltip } from 'bootstrap-vue'
import {
  BAlert,
  BCard,
  BCardHeader,
  BCardBody,
  BRow,
  BCol,
  BButton,
  BButtonGroup,
  BFormGroup,
  BFormInput,
  BFormSelect,
  BModal,
  BCollapse,
} from 'bootstrap-vue'
import {
  getOfferQuillOptions,
  mountOfferQuillExternalToolbar,
} from '@/views/apps/offer/offer-configure/offer-configure-quill'
import { quillEditor } from 'vue-quill-editor'
import OfferSectionPicker from '../OfferSectionPicker.vue'
import OfferSignatureSettings from '../OfferSignatureSettings.vue'
import OfferSectionPageControls from '../OfferSectionPageControls.vue'

export default {
  name: 'OfferConfigureStepLetter',
  directives: {
    'b-tooltip': VBTooltip,
  },
  components: {
    BAlert,
    BCard,
    BCardHeader,
    BCardBody,
    BRow,
    BCol,
    BButton,
    BButtonGroup,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BModal,
    BCollapse,
    quillEditor,
    OfferSectionPicker,
    OfferSignatureSettings,
    OfferSectionPageControls,
  },
  props: {
    templateEditor: {
      type: Object,
      required: true,
    },
    letterSectionPickerItems: {
      type: Array,
      required: true,
    },
    includedOrderedLetterSections: {
      type: Array,
      required: true,
    },
    activeLetterSection: {
      type: String,
      default: '',
    },
    letterTitleText: {
      type: String,
      default: '',
    },
    layoutDefaults: {
      type: Object,
      required: true,
    },
    fontSizeSelectOptions: {
      type: Array,
      required: true,
    },
    titleAlignOptions: {
      type: Array,
      required: true,
    },
    defaultSectionTitleText: {
      type: Object,
      required: true,
    },
    showLetterSettingsModal: {
      type: Boolean,
      default: false,
    },
    offerSignatureLocationsModel: {
      type: Array,
      default: () => [],
    },
    employeeSignPlacement: {
      type: Object,
      required: true,
    },
    editorMountKey: {
      type: Number,
      default: 0,
    },
    formId: {
      type: [String, Number],
      default: null,
    },
    editorDisabled: {
      type: Boolean,
      default: false,
    },
    sectionLayoutResolved: {
      type: Function,
      required: true,
    },
    sectionPageStartMode: {
      type: Function,
      required: true,
    },
    letterAccordionId: {
      type: Function,
      required: true,
    },
  },
  computed: {
    offerQuillOptions() {
      return getOfferQuillOptions()
    },
  },
  watch: {
    activeLetterSection() {
      this.$nextTick(() => this.decorateVisibleQuillToolbars())
    },
    editorMountKey() {
      this.$nextTick(() => this.decorateVisibleQuillToolbars())
    },
  },
  mounted() {
    this.$nextTick(() => this.decorateVisibleQuillToolbars())
  },
  methods: {
    onEmployeeSignChange(prop, value) {
      this.$emit('employee-sign-change', prop, value)
    },
    onLetterSectionHeaderClick(sectionKey) {
      this.$emit('accordion-header-click', sectionKey)
    },
    quillRefName(fieldKey) {
      return `ocQuill-${fieldKey}`
    },
    quillToolbarId(fieldKey) {
      return `ocQuillToolbar-${fieldKey}`
    },
    resolveQuillEditor(fieldKey) {
      const ref = this.$refs[this.quillRefName(fieldKey)]
      return Array.isArray(ref) ? ref[0] : ref
    },
    mountQuillToolbar(fieldKey) {
      const editor = this.resolveQuillEditor(fieldKey)
      const toolbarSlot = document.getElementById(this.quillToolbarId(fieldKey))
      if (editor && editor.$el && toolbarSlot) {
        mountOfferQuillExternalToolbar(editor.$el, toolbarSlot)
      }
    },
    onQuillReady(fieldKey) {
      this.$nextTick(() => this.mountQuillToolbar(fieldKey))
    },
    decorateVisibleQuillToolbars() {
      Object.keys(this.$refs).forEach(refKey => {
        if (!refKey.startsWith('ocQuill-')) return
        const fieldKey = refKey.slice('ocQuill-'.length)
        this.mountQuillToolbar(fieldKey)
      })
    },
  },
}
</script>

<style scoped>
.oc-form-section {
  background: #f8f9fb;
  border: 1px solid #eef0f3;
  border-radius: 0.5rem;
  padding: 0.85rem 1rem;
}

.oc-form-section__head {
  display: flex;
  align-items: center;
  font-size: 0.8125rem;
  font-weight: 700;
  color: #2c2c2c;
}

.oc-settings-btn {
  white-space: nowrap;
  font-weight: 600;
}

.oc-empty-sections-alert {
  border: 1px dashed #d8d6de;
  display: flex;
  align-items: center;
}

.oc-letter-section-card {
  border: 1px solid #eef0f3;
  border-radius: 0.5rem;
  overflow: hidden;
  transition: border-color 0.15s, box-shadow 0.15s;
}

.oc-letter-section-card--open {
  border-color: rgba(0, 186, 176, 0.45);
  box-shadow: 0 2px 8px rgba(0, 186, 176, 0.1);
  overflow: visible;
}

.oc-letter-section-header {
  background: #fff;
  border-bottom: 1px solid transparent;
  cursor: pointer;
}

.oc-letter-section-card--open .oc-letter-section-header {
  background: #f8fffe;
  border-bottom-color: #eef0f3;
}

.oc-letter-section-num {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.5rem;
  height: 1.5rem;
  border-radius: 50%;
  background: rgba(0, 186, 176, 0.12);
  color: #00bab0;
  font-size: 0.6875rem;
  font-weight: 700;
  flex-shrink: 0;
}

.oc-letter-section-title {
  font-weight: 600;
  font-size: 0.875rem;
  color: #2c2c2c;
  text-align: left;
}

.oc-letter-section-body {
  background: #fff;
  padding: 0.85rem 1rem;
  min-height: 12rem;
}

.oc-move-btn {
  padding: 0.2rem 0.35rem;
}

.oc-letter-section-chevron {
  flex-shrink: 0;
}

.oc-letter-sections .collapse.show {
  overflow: visible;
}
</style>

<style lang="scss">
@import '@core/scss/vue/libs/quill.scss';

.offer-configure-modal .offer-quill-toolbar-slot .ql-offerPageBreak {
  width: 2rem !important;
  height: 2rem !important;
  min-height: 0;
  padding: 0 !important;
  border: none;
  border-radius: 0.25rem;
}

.offer-configure-modal .offer-quill-toolbar-slot .ql-offerPageBreak::after {
  content: none;
}

.offer-configure-modal .offer-quill-editor .offer-manual-page-break:not(.offer-page-break) {
  display: block;
  margin: 14px 0;
  padding: 6px 0;
  text-align: center;
  color: #6c757d;
  font-size: 11px;
  user-select: none;
}

.offer-configure-modal .offer-quill-editor .offer-manual-page-break:not(.offer-page-break) hr {
  border: 0;
  border-top: 2px dashed #adb5bd;
  margin: 0 0 4px;
}
</style>
